import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { CaretRight } from "components/icons/CaretRight";
import { Fragment } from "react";
import { OpenItem } from "types/Models/openItem";
import { OpenItemClarificationInput } from "./OpenItemClarificationInput";
import { OpenItemInvoiceInput } from "./OpenItemInvoiceInput";

type MobileOpenItemListProps = {
  openItems: OpenItem[];
};

type MobileOpenItemCardProps = {
  openItem: OpenItem;
  position: number;
};

const OpenItemAmount = ({ amount }: { amount: OpenItem["amount"] }) => {
  if (!amount) return null;

  if (isNaN(Number(amount))) return <>{amount}</>;

  return <AmountSuperScript amount={Number(amount)} />;
};

const MobileOpenItemCard = ({
  openItem,
  position,
}: MobileOpenItemCardProps) => {
  const { uuid, merchant, date, bank_name, description, information_request } =
    openItem;

  const details = [
    {
      key: "amount",
      value: openItem.amount && <OpenItemAmount amount={openItem.amount} />,
    },
    { key: "vendor", value: merchant },
    { key: "date", value: date },
    { key: "bank", value: bank_name },
  ].filter(({ value }) => Boolean(value));

  return (
    <Accordion.RawItem
      value={uuid}
      className="t-rounded-lg t-border t-border-solid t-border-neutral-0 t-bg-surface-lighter-grey"
    >
      <Accordion.RawTrigger className="all:unset t-group t-flex t-w-full t-box-border t-flex-col t-gap-2 t-p-3 t-cursor-pointer">
        <span className="t-flex t-items-center t-justify-between">
          <span className="t-flex t-size-5 t-items-center t-justify-center t-rounded-full t-bg-neutral-10 t-text-caption t-text-text-60">
            {position}
          </span>
          <span className="t-flex t-text-text-30 t-transition-transform group-data-state-open:-t-rotate-90">
            <CaretRight />
          </span>
        </span>
        <span className="t-text-body t-text-text-100 t-break-words">
          {information_request || "-"}
        </span>
      </Accordion.RawTrigger>

      <Accordion.RawContent className="t-flex t-flex-col t-gap-3 t-px-3 t-pb-3">
        {details.length > 0 && (
          <div className="t-flex t-flex-wrap t-items-center t-gap-x-1.5 t-text-body-sm t-text-text-30">
            {details.map(({ key, value }, index) => (
              <Fragment key={key}>
                {index > 0 && <span>•</span>}
                <span>{value}</span>
              </Fragment>
            ))}
          </div>
        )}
        {description && (
          <p className="t-m-0 -t-mt-2 t-text-body-sm t-text-text-30 t-break-words">
            {description}
          </p>
        )}
        <OpenItemClarificationInput openItem={openItem} label="Clarification" />
        <div className="t-flex t-flex-col t-gap-1">
          <span className="t-text-body-sm t-text-text-60">Invoice</span>
          <OpenItemInvoiceInput openItem={openItem} variant="default" />
        </div>
      </Accordion.RawContent>
    </Accordion.RawItem>
  );
};

export const MobileOpenItemList = ({ openItems }: MobileOpenItemListProps) => {
  const defaultOpenItem = openItems[0]?.uuid;

  return (
    <Accordion.Root
      type="single"
      collapsible
      defaultValue={defaultOpenItem}
      className="t-flex t-flex-col t-gap-3"
    >
      {openItems.map((openItem, index) => (
        <MobileOpenItemCard
          key={openItem.uuid}
          openItem={openItem}
          position={index + 1}
        />
      ))}
    </Accordion.Root>
  );
};
